"use client";
import { jsx } from "react/jsx-runtime";
import { C_LOC_COOKIE } from "../../../constants/cookies.js";
import Cookie from "js-cookie";
import { CRITEO_USER_COOKIE } from "../../../utils/criteo/constants.js";
import { generateCriteoCookie } from "../../../utils/criteo/utils.js";
import { decodeObjectFromBase64, encodeObjectToBase64 } from "../../../utils/index.js";
import { createContext, useMemo, useCallback, useReducer, useEffect } from "react";
import { useCookie } from "../../../hooks/useCookie/index.js";
import { useSessionStorage } from "../../../hooks/useSessionStorage/index.js";
import { MINI_BANNER_DETAILS_SESSION_STORAGE_KEY, STORE_LOCATION_RESET_SESSION_STORAGE_FLAG, WAREHOUSE_RESET_SESSION_STORAGE_FLAG, LEGACY_COOKIE_ONE_TRUST, LEGACY_COOKIE_WC_BD_WHS_DEFAULT_VALUE, LEGACY_COOKIE_WC_BD_WHS, LEGACY_COOKIE_WC_BD_ZIP } from "./constants.js";
import { thirdPartyInfoInitialState, getInitialDeliveryLocationState, getInitialWcMemberState, getInitialUserLocationState, getInitialWarehouseState, getInitialUserPreferencesState, getInitialOrderPrice } from "./defaultState.js";
import { deliveryLocation, wc_member, wcCartOrder, storeLocation, warehouse, oneTrust } from "./legacyCookieStorage.js";
import { userPreferences } from "./localStorage.js";
import { ActionType } from "./types.js";
function reducer(state, action) {
  switch (action.type) {
    case ActionType.setCriteoUserCookie:
      return { ...state, criteoUserCookie: action.payload };
    case ActionType.setDeliveryLocationState:
      return {
        ...state,
        deliveryLocationState: {
          ...state.deliveryLocationState,
          ...action.payload
        }
      };
    case ActionType.setIsInitialized:
      return { ...state, isInitialized: action.payload };
    case ActionType.setSelectedWarehouse:
      return {
        ...state,
        selectedWarehouseState: {
          ...state.selectedWarehouseState,
          ...action.payload
        }
      };
    case ActionType.setThirdPartyInfo:
      return {
        ...state,
        thirdPartyInfo: { ...state.thirdPartyInfo, ...action.payload }
      };
    case ActionType.setUserLocation:
      return {
        ...state,
        userLocation: { ...state.userLocation, ...action.payload }
      };
    case ActionType.setUserPreferences:
      return {
        ...state,
        userPreferences: {
          ...state.userPreferences,
          ...action.payload
        }
      };
    case ActionType.setWcMemberState:
      return { ...state, wcMemeberState: action.payload };
    case ActionType.setWcCartOrder:
      return { ...state, wcCartOrder: action.payload };
    case ActionType.setMiniBannerDetails:
      return { ...state, miniBannerDetails: action.payload };
    default:
      return state;
  }
}
const BrowseContext = createContext(null);
const BrowseContextProvider = ({
  children,
  initialValues,
  locale
}) => {
  var _a;
  const isCanada = locale === "en-ca" || locale === "fr-ca";
  const [
    miniBannerDetailsSessionStorageRaw,
    setMiniBannerDetailsSessionStorageRaw
  ] = useSessionStorage(MINI_BANNER_DETAILS_SESSION_STORAGE_KEY, null);
  const miniBannerDetailsSessionStorage = useMemo(() => {
    const raw = typeof miniBannerDetailsSessionStorageRaw === "string" ? miniBannerDetailsSessionStorageRaw : null;
    try {
      return decodeObjectFromBase64(raw);
    } catch (error) {
      console.error("Failed to decode MiniBannerResponse from session storage:", error);
      return null;
    }
  }, [miniBannerDetailsSessionStorageRaw]);
  const setMiniBannerDetailsSessionStorage = useCallback(
    (value) => {
      if (value == null) {
        setMiniBannerDetailsSessionStorageRaw(null);
      } else {
        const encoded = encodeObjectToBase64(value);
        setMiniBannerDetailsSessionStorageRaw(encoded);
      }
    },
    [setMiniBannerDetailsSessionStorageRaw]
  );
  const [state, dispatch] = useReducer(reducer, {
    criteoUserCookie: "",
    deliveryLocationState: {
      city: "",
      state: "",
      zipCode: "",
      ...initialValues == null ? void 0 : initialValues.deliveryLocation
    },
    isInitialized: false,
    selectedWarehouseState: {
      distributionCenters: [],
      groceryCenters: [],
      ...initialValues == null ? void 0 : initialValues.warehouse
    },
    thirdPartyInfo: {
      isCookiesFullyLoaded: false,
      ...thirdPartyInfoInitialState,
      ...initialValues == null ? void 0 : initialValues.thirdPartyInfo
    },
    userLocation: {
      city: "",
      zip: "",
      ...initialValues == null ? void 0 : initialValues.userLocation
    },
    userPreferences: {
      currency: "",
      locale: "",
      ...initialValues == null ? void 0 : initialValues.userPreferences
    },
    miniBannerDetails: miniBannerDetailsSessionStorage,
    wcCartOrder: 0,
    wcMemeberState: ""
  });
  const {
    isInitialized,
    deliveryLocationState,
    wcMemeberState,
    userLocation,
    selectedWarehouseState,
    userPreferences: userPreferences$1,
    criteoUserCookie,
    thirdPartyInfo,
    wcCartOrder: cartOrder,
    miniBannerDetails
  } = state;
  const updateMiniBannerDetails = useCallback(
    (newMiniBannerDetails) => {
      const normalizedDetails = newMiniBannerDetails ? {
        ...newMiniBannerDetails,
        deliverySurcharge: Number(
          newMiniBannerDetails.deliverySurcharge
        ),
        freeDeliveryOrderAmount: Number(
          newMiniBannerDetails.freeDeliveryOrderAmount
        )
      } : newMiniBannerDetails;
      setMiniBannerDetailsSessionStorage(normalizedDetails);
      dispatch({
        payload: normalizedDetails,
        type: ActionType.setMiniBannerDetails
      });
    },
    [dispatch, setMiniBannerDetailsSessionStorage]
  );
  const updateDeliveryLocationState = useCallback(
    (newDeliveryLocationState) => {
      deliveryLocation.write(newDeliveryLocationState);
      dispatch({
        payload: newDeliveryLocationState,
        type: ActionType.setDeliveryLocationState
      });
    },
    [dispatch]
  );
  const updateWcMemberState = useCallback(
    (newWcMemberValue) => {
      wc_member.write(newWcMemberValue);
      dispatch({
        payload: newWcMemberValue,
        type: ActionType.setWcMemberState
      });
    },
    [dispatch]
  );
  const updateWcCartOrderState = useCallback(
    (newWcCartOrder) => {
      if (cartOrder === newWcCartOrder) {
        return;
      }
      wcCartOrder.write(newWcCartOrder);
      dispatch({
        payload: newWcCartOrder,
        type: ActionType.setWcCartOrder
      });
    },
    [dispatch, cartOrder]
  );
  const updateUserLocationState = useCallback(
    (newLocation) => {
      const updatedLocation = { ...userLocation, ...newLocation };
      storeLocation.write(updatedLocation);
      dispatch({
        payload: updatedLocation,
        type: ActionType.setUserLocation
      });
      window.sessionStorage.setItem(
        STORE_LOCATION_RESET_SESSION_STORAGE_FLAG,
        "true"
      );
    },
    [dispatch, userLocation]
  );
  const updateWarehouseState = useCallback(
    (newWarehouse) => {
      const updatedWarehouse = {
        ...selectedWarehouseState,
        ...newWarehouse
      };
      warehouse.write(updatedWarehouse);
      dispatch({
        payload: updatedWarehouse,
        type: ActionType.setSelectedWarehouse
      });
      window.sessionStorage.setItem(
        WAREHOUSE_RESET_SESSION_STORAGE_FLAG,
        "true"
      );
    },
    [dispatch, selectedWarehouseState]
  );
  const updateUserPreferencesState = useCallback(
    async (newPreferences) => {
      const updatedPreferences = {
        ...userPreferences$1,
        ...newPreferences
      };
      dispatch({
        payload: updatedPreferences,
        type: ActionType.setUserPreferences
      });
      await userPreferences.write(updatedPreferences);
    },
    [dispatch, userPreferences$1]
  );
  const updateThirdPartyInfoState = useCallback(
    (newInfo) => {
      dispatch({
        payload: {
          ...thirdPartyInfo,
          ...newInfo
        },
        type: ActionType.setThirdPartyInfo
      });
    },
    [dispatch, thirdPartyInfo]
  );
  useEffect(() => {
    const asyncFn = async () => {
      if (typeof window !== "undefined") {
        dispatch({
          payload: getInitialDeliveryLocationState(),
          type: ActionType.setDeliveryLocationState
        });
        dispatch({
          payload: getInitialWcMemberState(),
          type: ActionType.setWcMemberState
        });
        dispatch({
          payload: {
            ...getInitialUserLocationState(),
            ...initialValues == null ? void 0 : initialValues.userLocation
          },
          type: ActionType.setUserLocation
        });
        dispatch({
          payload: {
            ...getInitialWarehouseState(),
            ...initialValues == null ? void 0 : initialValues.warehouse
          },
          type: ActionType.setSelectedWarehouse
        });
        const initialUserPreferencesState = await getInitialUserPreferencesState();
        dispatch({
          payload: {
            ...initialUserPreferencesState,
            ...initialValues == null ? void 0 : initialValues.userPreferences
          },
          type: ActionType.setUserPreferences
        });
        dispatch({
          payload: true,
          type: ActionType.setIsInitialized
        });
        dispatch({
          payload: getInitialOrderPrice(),
          type: ActionType.setWcCartOrder
        });
      }
    };
    asyncFn();
  }, [typeof window]);
  useEffect(() => {
    let oneTrustCompletelyLoaded = false;
    const oneTrustCookieinitialUpdate = setInterval(() => {
      if (!oneTrustCompletelyLoaded) {
        const oneTrustCookie = Cookie.get(LEGACY_COOKIE_ONE_TRUST);
        if (oneTrustCookie == null ? void 0 : oneTrustCookie.includes("groups")) {
          dispatch({
            payload: {
              isCookiesFullyLoaded: true,
              consentManagement: oneTrust.read()
            },
            type: ActionType.setThirdPartyInfo
          });
          oneTrustCompletelyLoaded = true;
        }
      }
    }, 100);
    return () => clearInterval(oneTrustCookieinitialUpdate);
  }, []);
  useEffect(() => {
    const updateOneTrustContext = () => {
      const consentManagementState = thirdPartyInfo.consentManagement;
      const oneTrustCookie = oneTrust.read();
      if (consentManagementState.isTargetingEnabled !== oneTrustCookie.isTargetingEnabled) {
        window.location.reload();
      }
    };
    window.addEventListener("OneTrustGroupsUpdated", updateOneTrustContext);
    return () => {
      window.removeEventListener(
        "OneTrustGroupsUpdated",
        updateOneTrustContext
      );
    };
  }, [thirdPartyInfo.consentManagement]);
  useEffect(() => {
    var _a2;
    if (typeof window !== "undefined" && (thirdPartyInfo == null ? void 0 : thirdPartyInfo.isCookiesFullyLoaded) && (((_a2 = thirdPartyInfo == null ? void 0 : thirdPartyInfo.consentManagement) == null ? void 0 : _a2.isTargetingEnabled) || isCanada)) {
      const currentCriteoUserCookie = Cookie.get(CRITEO_USER_COOKIE);
      dispatch({
        payload: currentCriteoUserCookie || generateCriteoCookie(),
        type: ActionType.setCriteoUserCookie
      });
    }
  }, [
    isCanada,
    (_a = thirdPartyInfo == null ? void 0 : thirdPartyInfo.consentManagement) == null ? void 0 : _a.isTargetingEnabled,
    thirdPartyInfo == null ? void 0 : thirdPartyInfo.isCookiesFullyLoaded
  ]);
  const [bdWarehouseNumber, setBdWarehouseNumber] = useCookie(
    LEGACY_COOKIE_WC_BD_WHS,
    LEGACY_COOKIE_WC_BD_WHS_DEFAULT_VALUE
  );
  const [bdDeliveryLocation, setBDDeliveryLocation] = useCookie(
    LEGACY_COOKIE_WC_BD_ZIP
  );
  const [bdLocation] = useCookie(C_LOC_COOKIE);
  const miniBannerDetailsObj = useMemo(() => {
    return {
      setMiniBannerDetails: updateMiniBannerDetails,
      values: miniBannerDetails
    };
  }, [miniBannerDetails, updateMiniBannerDetails]);
  const bdWarehouseNumberObj = useMemo(() => {
    return {
      updateBdWarehouseNumber: setBdWarehouseNumber,
      value: bdWarehouseNumber || ""
    };
  }, [bdWarehouseNumber, setBdWarehouseNumber]);
  const bdDeliveryLocationObj = useMemo(() => {
    return {
      updateDeliveryLocation: ({ zipCode }) => setBDDeliveryLocation(zipCode),
      zipCode: bdDeliveryLocation ?? "",
      bdLocation: bdLocation ?? ""
    };
  }, [bdDeliveryLocation, setBDDeliveryLocation, bdLocation]);
  const deliveryLocationObj = useMemo(() => {
    return {
      city: deliveryLocationState == null ? void 0 : deliveryLocationState.city,
      state: deliveryLocationState == null ? void 0 : deliveryLocationState.state,
      updateDeliveryLocation: updateDeliveryLocationState,
      zipCode: deliveryLocationState == null ? void 0 : deliveryLocationState.zipCode
    };
  }, [
    deliveryLocationState == null ? void 0 : deliveryLocationState.city,
    deliveryLocationState == null ? void 0 : deliveryLocationState.state,
    deliveryLocationState == null ? void 0 : deliveryLocationState.zipCode,
    updateDeliveryLocationState
  ]);
  const thirdPartyInfoObj = useMemo(() => {
    return {
      ...thirdPartyInfo,
      updateThirdPartyInfo: updateThirdPartyInfoState
    };
  }, [thirdPartyInfo, updateThirdPartyInfoState]);
  const userLocationObj = useMemo(() => {
    return {
      ...userLocation,
      storeLocCookieExpResetFlag: storeLocation.storeLocCookieExpResetFlag,
      updateUserLocation: updateUserLocationState
    };
  }, [userLocation, updateUserLocationState]);
  const userPreferencesObj = useMemo(() => {
    return {
      ...userPreferences$1,
      updateUserPreferences: updateUserPreferencesState
    };
  }, [userPreferences$1, updateUserPreferencesState]);
  const warehouseObj = useMemo(() => {
    return {
      ...selectedWarehouseState,
      updateWarehouse: updateWarehouseState,
      warehouseCookieExpResetFlag: warehouse.warehouseCookieExpResetFlag
    };
  }, [selectedWarehouseState, updateWarehouseState]);
  const wcMemberObj = useMemo(() => {
    return {
      updateWcMember: updateWcMemberState,
      value: wcMemeberState
    };
  }, [updateWcMemberState, wcMemeberState]);
  const wcCartOrderObj = useMemo(() => {
    return {
      updateWcCartOrder: updateWcCartOrderState,
      value: cartOrder
    };
  }, [updateWcCartOrderState, cartOrder]);
  const context = useMemo(() => {
    return {
      miniBannerDetails: miniBannerDetailsObj,
      bdWarehouseNumber: bdWarehouseNumberObj,
      bdDeliveryLocation: bdDeliveryLocationObj,
      criteoUserCookie,
      deliveryLocation: deliveryLocationObj,
      isInitialized,
      locale,
      thirdPartyInfo: thirdPartyInfoObj,
      userLocation: userLocationObj,
      userPreferences: userPreferencesObj,
      warehouse: warehouseObj,
      wcMember: wcMemberObj,
      wcCartOrder: wcCartOrderObj
    };
  }, [
    miniBannerDetailsObj,
    bdWarehouseNumberObj,
    bdDeliveryLocationObj,
    criteoUserCookie,
    deliveryLocationObj,
    isInitialized,
    locale,
    thirdPartyInfoObj,
    userLocationObj,
    userPreferencesObj,
    warehouseObj,
    wcMemberObj,
    wcCartOrderObj
  ]);
  return /* @__PURE__ */ jsx(BrowseContext.Provider, { value: context, children });
};
BrowseContext.displayName = "BrowseContext";
BrowseContextProvider.displayName = "BrowseContextProvider";
export {
  BrowseContext,
  BrowseContextProvider
};
